import React from 'react';
import { AreaChart as RechartsAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { COLORS, formatYAxisNumber } from '../utils/ChartConstants';
import { CustomTooltip } from '../utils/CustomTooltip';

interface AreaChartProps {
  data: any[];
}

// Custom tick component that wraps text or tilts based on width
const CustomWrappedTick = (props: any) => {
  const { x, y, payload, width, shouldTilt } = props;

  if (!payload?.value) return null;

  const text = String(payload.value);

  // If shouldTilt is true, tilt all labels with wrapping
  if (shouldTilt) {
    const fontSize = 11;
    const maxCharsPerLine = 20; // Maximum characters per line for tilted text

    // Split text into lines
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    // Limit to 2-3 lines for tilted text
    const displayLines = lines.slice(0, 3);

    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#666"
        fontSize={fontSize}
        transform={`rotate(-45 ${x} ${y})`}
      >
        {displayLines.map((line, index) => (
          <tspan
            key={index}
            x={x}
            dy={index === 0 ? '0.71em' : '1.1em'}
            dx="-0.5em"
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  }

  // Otherwise, wrap text within point width
  const pointWidth = width || 60;
  const fontSize = 12;
  const charWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor((pointWidth - 4) / charWidth);

  // Function to break word with hyphen if needed
  const breakWord = (word: string, maxLength: number): string[] => {
    if (word.length <= maxLength) return [word];

    const parts: string[] = [];
    let remaining = word;

    while (remaining.length > 0) {
      if (remaining.length <= maxLength) {
        parts.push(remaining);
        break;
      }
      const breakPoint = maxLength - 1;
      parts.push(remaining.substring(0, breakPoint) + '-');
      remaining = remaining.substring(breakPoint);
    }

    return parts;
  };

  // Process text into lines
  const lines: string[] = [];
  const words = text.split(' ');
  let currentLine = '';

  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }
      const brokenWords = breakWord(word, maxCharsPerLine);
      lines.push(...brokenWords);
    } else {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
  }
  if (currentLine) lines.push(currentLine);

  // Limit to max 3 lines
  const displayLines = lines.slice(0, 3);
  if (lines.length > 3) {
    const lastLine = displayLines[2];
    if (lastLine.length > maxCharsPerLine - 3) {
      displayLines[2] = lastLine.substring(0, maxCharsPerLine - 3) + '...';
    } else {
      displayLines[2] = lastLine + '...';
    }
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill="#666"
      fontSize={fontSize}
    >
      {displayLines.map((line, index) => (
        <tspan
          key={index}
          x={x}
          dy={index === 0 ? '0.71em' : '1.2em'}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

export const AreaChart: React.FC<AreaChartProps> = ({ data }) => {
  // Calculate approximate point spacing based on data count
  // Use a more conservative estimate for small screens
  const estimatedPointWidth = Math.max(30, 300 / data.length);

  // Check if any label would overflow when wrapped
  // Use a more conservative check - tilt if points are close or text is long
  const shouldTiltLabels = React.useMemo(() => {
    // Always tilt if we have more than 3 data points or point width is less than 100px
    if (data.length > 3 || estimatedPointWidth < 100) return true;

    const fontSize = 12;
    const charWidth = fontSize * 0.55;
    const maxCharsPerLine = Math.floor((estimatedPointWidth - 4) / charWidth);

    return data.some(item => {
      if (!item.name) return false;
      const text = String(item.name);

      // Check if text is too long overall or any word is too long
      if (text.length > maxCharsPerLine * 2) return true;

      const words = text.split(' ');
      return words.some(word => word.length > maxCharsPerLine);
    });
  }, [data, estimatedPointWidth]);

  const bottomMargin = shouldTiltLabels ? 60 : 0;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsAreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: bottomMargin }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          tick={<CustomWrappedTick width={estimatedPointWidth} shouldTilt={shouldTiltLabels} />}
          interval={0}
          height={80}
        />
        <YAxis tickFormatter={formatYAxisNumber} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.3} />
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
};